<template>
  <div>
    <!-- 加载动画 -->
    <div v-if="loading" class="loading-overlay" :class="{ 'fade-out': loadingComplete }">
      <div class="loading-content">
        <div class="spinner"></div>
        <p class="loading-title">{{ loadingStages[currentStage].title }}</p>
        <p class="loading-subtitle">{{ loadingStages[currentStage].subtitle }}</p>
      </div>
    </div>

    <!-- 主内容 -->
    <div v-else class="monthly-report">
    <!-- 模块1 -->
    <div class="module">
      <h2 class="module-title">{{ $t('monthlyReport.trendTitle') }}</h2>
      <div class="module-content">
        <div ref="trendChart" class="chart-container"></div>
        <div class="stats-row" v-if="trendData">
          <div class="stat-item">
            <div class="stat-label">{{ $t('monthlyReport.highestPoint') }}</div>
            <div class="stat-value stat-high">{{ formatScore(trendData.statistics.max_score) }}</div>
            <div class="stat-note">{{ $t('monthlyReport.dailyAverage') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">{{ $t('monthlyReport.lowestPoint') }}</div>
            <div class="stat-value stat-low">{{ formatScore(trendData.statistics.min_score) }}</div>
            <div class="stat-note">{{ $t('monthlyReport.dailyAverage') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">{{ $t('monthlyReport.averagePoint') }}</div>
            <div class="stat-value stat-avg">{{ formatScore(trendData.statistics.avg_score) }}</div>
          </div>
        </div>
        <div class="ai-interpretation" v-if="trendData && trendData.ai_interpretation">
          <h3 class="ai-title">{{ $t('monthlyReport.aiInterpretation') }}</h3>
          <p class="ai-text" v-html="processAIText(trendData.ai_interpretation.text, trendData.ai_interpretation.highlights)"></p>
        </div>
        <div class="divider"></div>
        <div class="additional-content" v-if="monitoringStats">
          <div class="monitoring-stats">
            <div class="stats-left">
              <div class="stats-title">{{ $t('monthlyReport.monitoringStatsTitle', { month }) }}</div>
              <div class="stats-numbers">
                <span class="monitoring-count">{{ monitoringStats.monitoring_count }}</span>
                <span class="divider-slash">/</span>
                <span class="total-days">{{ monitoringStats.total_days }}</span>
                <span class="days-unit">{{ $t('monthlyReport.daysUnit') }}</span>
              </div>
              <div class="stats-desc">{{ $t('monthlyReport.effectiveDays') }}</div>
              <div class="status-message" v-if="monitoringStats.monitoring_status" v-html="processAIText(monitoringStats.monitoring_status.message, monitoringStats.monitoring_status.highlights)"></div>
            </div>
            <div class="stats-right">
              <div class="date-range">{{ formatMonthDay(monitoringStats.time_range.start_date) }} - {{ formatMonthDay(monitoringStats.time_range.end_date) }}</div>
              <div class="trajectory-text">{{ $t('monthlyReport.monitoringTrack', { days: monitoringStats.total_days }) }}</div>
              <div class="trajectory-dots" v-if="monitoringStats.monitoring_track">
                <div 
                  v-for="(monitored, index) in monitoringStats.monitoring_track" 
                  :key="index"
                  class="dot"
                  :class="{ 'monitored': monitored }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块2 -->
    <div class="module">
      <h2 class="module-title">{{ $t('monthlyReport.focusTitle') }}</h2>

      <!-- 切换栏 -->
      <div class="focus-switch-bar">
        <div class="switch-item switch-left" :class="{ 'active': activeTab === 'decline', 'inactive': activeTab !== 'decline' }" @click="switchTab('decline')">
          <div class="switch-title">{{ $t('monthlyReport.declineTitle') }}</div>
          <div class="switch-subtitle">{{ $t('monthlyReport.declineSubtitle') }}</div>
        </div>
        <div class="switch-item switch-right" :class="{ 'active': activeTab === 'rise', 'inactive': activeTab !== 'rise' }" @click="switchTab('rise')">
          <div class="switch-title">{{ $t('monthlyReport.riseTitle') }}</div>
          <div class="switch-subtitle">{{ $t('monthlyReport.riseSubtitle') }}</div>
        </div>
      </div>

      <div class="module-content focus-module-content">
        <div class="health-focus-top" :class="{ 'decline-style': activeTab === 'decline', 'rise-style': activeTab === 'rise' }" v-if="healthFocusData">
          <div class="insight-label">{{ $t('monthlyReport.insightLabel') }}</div>
          <div class="insight-content" v-if="healthFocusData.insight" v-html="processAIText(healthFocusData.insight.text, healthFocusData.insight.highlights)"></div>
        </div>
        <div ref="focusChart" class="focus-chart-container"></div>
        <div class="chart-legend" v-if="healthFocusData">
          <div class="legend-item">
            <img src="@/assets/Moon_total.png" class="legend-icon" :alt="$t('monthlyReport.totalScore')">
            <span class="legend-text" style="color: #E0E0E0;">{{ $t('monthlyReport.totalScore') }}</span>
          </div>
          <div class="legend-item">
            <img :src="activeTab === 'decline' ? require('@/assets/Moon_constitution.png') : require('@/assets/Moon_constitution_blue.png')" class="legend-icon" :alt="$t('monthlyReport.constitution')">
            <span class="legend-text" :style="{ color: activeTab === 'decline' ? '#FFCFCA' : '#5087FF' }">{{ healthFocusData.constitution_line && healthFocusData.constitution_line[0] ? healthFocusData.constitution_line[0].name : $t('monthlyReport.constitution') }}</span>
          </div>
          <div class="legend-item">
            <img :src="activeTab === 'decline' ? require('@/assets/Moon_nutrition.png') : require('@/assets/Moon_nutrition_blue.png')" class="legend-icon" :alt="$t('monthlyReport.nutrition')">
            <span class="legend-text" :style="{ color: activeTab === 'decline' ? '#FF5C4D' : '#003CBF' }">{{ healthFocusData.nutrition_line && healthFocusData.nutrition_line[0] ? healthFocusData.nutrition_line[0].name : $t('monthlyReport.nutrition') }}</span>
          </div>
        </div>
        <div class="focus-summary" v-if="healthFocusData && healthFocusData.summary" v-html="processAIText(healthFocusData.summary.text, healthFocusData.summary.highlights)"></div>
      </div>
    </div>

    <!-- 模块3 -->
    <div class="module">
      <h2 class="module-title">{{ $t('monthlyReport.improvementPathTitle') }}</h2>
      <p class="module-subtitle">{{ $t('monthlyReport.improvementPathSubtitle') }}</p>
      <div class="module-content" v-if="improvementPathData">
        <div class="improvement-item" v-if="improvementPathData.avoid_risk">
          <p class="improvement-text" v-html="processAIText(improvementPathData.avoid_risk.text, improvementPathData.avoid_risk.highlights)"></p>
        </div>
        <div class="improvement-item" v-if="improvementPathData.consolidate">
          <p class="improvement-text" v-html="processAIText(improvementPathData.consolidate.text, improvementPathData.consolidate.highlights)"></p>
        </div>
        <div class="improvement-item" v-if="improvementPathData.detail">
          <p class="improvement-text" v-html="processAIText(improvementPathData.detail.text, improvementPathData.detail.highlights)"></p>
        </div>
        
        <div class="lifestyle-suggestions" v-if="suggestionData && suggestionData.length">
          <div class="suggestion-item" v-for="(item, index) in suggestionData" :key="index">
            <img class="suggestion-icon" :src="require(`@/assets/${item.type===1?'motion':item.type===2?'drinking':'sleep'}.png`)" />
            <div class="suggestion-content">
              <div class="suggestion-topic">{{ item.topic }}</div>
              <div class="suggestion-text">{{ item.content }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <!-- 一键定制解决方案按钮 -->
      <div class="custom-solution-button-container">
        <button class="custom-solution-button" @click="handleCustomSolution">{{ $t('actions.customSolution') }}</button>
      </div>

      <!-- 免责声明 -->
      <div class="disclaimer">
        <p>{{ $t('monthlyReport.disclaimer1') }}</p>
        <p>{{ $t('monthlyReport.disclaimer2') }}</p>
      </div>
    </div>

    <!-- 定制方案加载页面 -->
    <CustomSolutionLoader
      v-if="showSolutionLoader"
      @loading-complete="handleLoadingComplete"
    />

    <!-- 定制方案抽屉 -->
    <CustomSolutionDrawer
      :visible="showDrawer"
      @update:visible="showDrawer = $event"
      @submit="handleFormSubmit"
      @close="handleDrawerClose"
    />
  </div>
</template>

<script>
import { post } from '@/net/http';
import { isWechatEnvironment, navigateToMiniProgram } from '@/utils/wechat';
import CustomSolutionLoader from '@/components/CustomSolutionLoader';
import CustomSolutionDrawer from '@/components/CustomSolutionDrawer';

export default {
  name: 'MonthlyReport',
  components: {
    CustomSolutionLoader,
    CustomSolutionDrawer
  },
  computed: {
    loadingStages() {
      return [
        {
          title: this.$t('monthlyReport.loadingStages.stage1Title'),
          subtitle: this.$t('monthlyReport.loadingStages.stage1Subtitle')
        },
        {
          title: this.$t('monthlyReport.loadingStages.stage2Title'),
          subtitle: this.$t('monthlyReport.loadingStages.stage2Subtitle')
        },
        {
          title: this.$t('monthlyReport.loadingStages.stage3Title'),
          subtitle: this.$t('monthlyReport.loadingStages.stage3Subtitle')
        },
        {
          title: this.$t('monthlyReport.loadingStages.stage4Title'),
          subtitle: this.$t('monthlyReport.loadingStages.stage4Subtitle')
        }
      ];
    }
  },
  data() {
    return {
      userId: '',
      customerId: '',
      year: '',
      month: '',
      packageNo: '',
      trendData: null,
      chartInstance: null,
      monitoringStats: null,
      healthFocusData: null,
      focusChartInstance: null,
      improvementPathData: null,
      suggestionData: [],
      loading: true,
      loadedCount: 0,
      activeTab: 'decline', // 默认选中下滑期
      loadingComplete: false,
      currentStage: 0,
      showDrawer: false, // 控制抽屉显示
      isFirstLoad: true, // 标记是否首次加载
      showSolutionLoader: false, // 控制加载页面显示
      userFormData: null, // 用户填写的表单数据
      isThirdParty: false // 第三方跳转标识
    };
  },
  created() {
    // 从URL参数获取数据
    this.userId = this.$route.query.userId || '';
    // 兼容 customer_id 和 customerId 两种命名
    this.customerId = this.$route.query.customer_id || this.$route.query.customerId || '';
    this.year = this.$route.query.year || '';
    this.month = this.$route.query.month || '';
    this.packageNo = this.$route.query.packageNo || '';

    // 从URL参数读取第三方跳转标识
    this.isThirdParty = this.$route.query.isThirdParty === '1';

    console.log('月度报告页面参数:', {
      userId: this.userId,
      customerId: this.customerId,
      year: this.year,
      month: this.month,
      isThirdParty: this.isThirdParty
    });
    // 启动加载阶段切换
    this.startLoadingStages();
    // 获取数据
    this.fetchTrendData();
    this.fetchMonitoringStats();
    this.fetchHealthFocus();
    this.fetchImprovementPath();
    this.fetchLifestyleSuggestions();
  },
  mounted() {
    // 隐藏AI小助手
    this.hideAIChat();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    if (this.focusChartInstance) {
      this.focusChartInstance.dispose();
    }
    // 离开页面时恢复AI小助手
    this.showAIChat();
  },
  methods: {
    handleCustomSolution() {
      console.log('[月度报告] 点击一键定制解决方案按钮');
      console.log('[月度报告] 当前isThirdParty值:', this.isThirdParty);

      // 检查是否为第三方跳转
      if (this.isThirdParty) {
        console.log('[月度报告] 检测到第三方跳转，执行返回逻辑');
        this.handleThirdPartyReturn();
        return;
      }

      console.log('[月度报告] 非第三方跳转，显示抽屉');
      // 显示抽屉
      this.showDrawer = true;
    },

    /**
     * 处理第三方返回
     */
    handleThirdPartyReturn() {
      console.log('[月度报告] 用户点击返回按钮');

      // 严格的环境检测
      // eslint-disable-next-line no-undef
      if (typeof wx === 'undefined') {
        console.error('[月度报告] wx对象不存在');
        alert(this.$t('monthlyReport.messages.notInWechat'));
        return;
      }

      // eslint-disable-next-line no-undef
      if (!wx.miniProgram) {
        console.error('[月度报告] wx.miniProgram不存在');
        alert(this.$t('monthlyReport.messages.notInMiniProgram'));
        return;
      }

      // 检查环境变量
      const env = window.__wxjs_environment;
      if (env !== 'miniprogram') {
        console.error('[月度报告] 环境检测失败，env:', env);
        alert(this.$t('monthlyReport.messages.notInMiniProgramWebview'));
        return;
      }

      console.log('[月度报告] 环境检测通过，开始返回流程');

      // 准备报告数据
      const reportData = {
        status: 'success',
        reportType: 'monthly',
        year: this.year,
        month: this.month,
        reportUrl: window.location.href
      };

      console.log('[月度报告] 发送返回消息:', reportData);

      try {
        // 通过postMessage发送消息给小程序
        // eslint-disable-next-line no-undef
        wx.miniProgram.postMessage({
          data: {
            action: 'thirdPartyReturn',
            data: reportData
          }
        });

        console.log('[月度报告] postMessage发送成功');

        // 提示用户
        alert(this.$t('monthlyReport.messages.returnSoon'));

        // 延迟后返回小程序（触发bindmessage）
        setTimeout(() => {
          console.log('[月度报告] 开始执行navigateBack');
          // eslint-disable-next-line no-undef
          wx.miniProgram.navigateBack({
            success: () => {
              console.log('[月度报告] navigateBack成功');
            },
            fail: (err) => {
              console.error('[月度报告] navigateBack失败:', err);
              alert(this.$t('monthlyReport.messages.returnFailed', { message: err.errMsg }));
            }
          });
        }, 500);
      } catch (err) {
        console.error('[月度报告] 返回过程异常:', err);
        alert(this.$t('monthlyReport.messages.returnFailed', { message: err.message }));
      }
    },

    // 处理表单提交
    async handleFormSubmit(formData) {
      console.log('表单提交，收集到的问卷keys:', formData.selectedKeys);
      this.userFormData = formData;

      // 关闭抽屉
      this.showDrawer = false;

      try {
        // 显示加载页面
        this.showSolutionLoader = true;
      } catch (error) {
        console.error('提交失败:', error);
        this.showSolutionLoader = false;
        this.showErrorMessage(this.$t('monthlyReport.messages.submitFailed'));
      }
    },

    // 处理抽屉关闭
    handleDrawerClose() {
      console.log('抽屉关闭');
    },

    // 加载完成后的处理
    async handleLoadingComplete() {
      console.log('加载完成，准备跳转');

      try {
        // 检测微信环境
        if (!this.isWechatEnvironment()) {
          this.showSolutionLoader = false;
          this.showErrorMessage(this.$t('monthlyReport.messages.openInWechat'));
          return;
        }

        // 跳转到小程序
        await this.navigateToMiniProgram();

      } catch (error) {
        console.error('跳转失败:', error);
        this.showSolutionLoader = false;
        this.showErrorMessage(this.$t('monthlyReport.messages.navigateFailed'));
      }
    },

    // 检测微信环境
    isWechatEnvironment() {
      return isWechatEnvironment();
    },

    // 跳转到小程序页面
    async navigateToMiniProgram() {
      try {
        // 收集用户选择的问卷keys
        const selectedKeys = this.userFormData?.selectedKeys || [];

        // 将keys数组转为JSON字符串并编码
        const questionnaireParam = encodeURIComponent(JSON.stringify(selectedKeys));

        // 计算月度的开始和结束日期
        const startDate = `${this.year}-${String(this.month).padStart(2, '0')}-01`;
        const lastDay = new Date(this.year, this.month, 0).getDate();
        const endDate = `${this.year}-${String(this.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

        console.log('收集到的问卷keys:', selectedKeys);
        console.log('编码后的参数:', questionnaireParam);
        console.log('月度推荐参数:', {
          recommend_type: 'monthly',
          customer_id: this.customerId,
          start_date: startDate,
          end_date: endDate
        });

        // 准备传递给小程序的数据（月度推荐）
        const miniProgramData = {
          customer_id: this.customerId,
          timestamp: Date.now(),
          source: 'h5_monthly_report',
          recommend_type: 'monthly',
          start_date: startDate,
          end_date: endDate,
          userInfo: this.userFormData // 传递用户填写的信息
        };

        console.log('准备跳转到小程序，数据:', miniProgramData);

        // 使用工具类进行跳转，将questionnaire和月度参数添加到URL中
        const jumpPromise = navigateToMiniProgram({
          appId: 'wx8c05eb024a5db14a', // 小程序AppID
          path: `/pages/shop/evaluationPlan/evaluationPlan?recommend_type=monthly&customer_id=${this.customerId}&start_date=${startDate}&end_date=${endDate}&questionnaire=${questionnaireParam}&pro=1`,
          extraData: miniProgramData,
          envVersion: 'release' // 正式版
        });

        // 延迟一小段时间后开始跳转，确保加载动画完整显示
        setTimeout(() => {
          console.log('开始执行小程序跳转');
        }, 500);

        await jumpPromise;
        console.log('小程序页面跳转成功');

        // 跳转成功后，页面会被小程序接管，加载状态会通过页面可见性监听自动清理

      } catch (error) {
        console.error('跳转过程中出错:', error);
        throw error;
      }
    },

    // 显示错误消息
    showErrorMessage(message) {
      if (this.$toast) {
        this.$toast(message);
      } else {
        alert(message);
      }
    },

    switchTab(tab) {
      this.activeTab = tab;
      this.isFirstLoad = false; // 切换后标记为非首次加载
      console.log('切换到:', tab);

      // 根据切换状态重新获取健康焦点数据
      const trendType = tab === 'decline' ? 'decline' : 'recovery';
      this.fetchHealthFocus(trendType);
    },

    startLoadingStages() {
      setTimeout(() => {
        if (this.loadedCount >= 2 && this.currentStage === 0) {
          this.currentStage = 1;
        }
      }, 2000);
      
      setTimeout(() => {
        if (this.loadedCount >= 4 && this.currentStage === 1) {
          this.currentStage = 2;
        }
      }, 4000);
    },
    
    checkAllLoaded() {
      this.loadedCount++;
      
      if (this.loadedCount >= 2 && this.currentStage === 0) {
        this.currentStage = 1;
      }
      
      if (this.loadedCount >= 4 && this.currentStage === 1) {
        this.currentStage = 2;
      }
      
      if (this.loadedCount >= 5) {
        this.currentStage = 3;
        setTimeout(() => {
          this.loadingComplete = true;
          setTimeout(() => {
            this.loading = false;
            this.$nextTick(() => {
              setTimeout(() => {
                if (this.trendData) this.initTrendChart();
                // 首次加载时渲染健康焦点图表
                if (this.isFirstLoad && this.healthFocusData) {
                  this.initFocusChart();
                }
              }, 100);
            });
          }, 500);
        }, 1000);
      }
    },
    
    async fetchLifestyleSuggestions() {
      try {
        const { get } = await import('@/net/http');
        const res = await get('/api/v1/report/interpret/suggestion', { 
          serial_number: this.packageNo 
        });
        
        if (res) {
          this.suggestionData = res;
        }
      } catch (error) {
        console.error('获取生活方式建议失败:', error);
      } finally {
        this.checkAllLoaded();
      }
    },
    
    async fetchImprovementPath() {
      try {
        const params = {
          user_id: parseInt(this.userId) || 1,
          year: parseInt(this.year) || new Date().getFullYear(),
          month: parseInt(this.month) || new Date().getMonth() + 1,
          trend_type: 'decline'
        };
        
        console.log('请求改善路径参数:', params);
        
        const res = await post('/api/v1/improvement-path', params);
        
        console.log('改善路径接口返回:', res);
        
        if (res) {
          this.improvementPathData = res;
          console.log('设置改善路径数据:', this.improvementPathData);
        }
      } catch (error) {
        console.error('获取改善路径数据失败:', error);
      } finally {
        this.checkAllLoaded();
      }
    },
    
    async fetchHealthFocus(trendType = 'decline') {
      try {
        const params = {
          user_id: parseInt(this.userId) || 1,
          year: parseInt(this.year) || new Date().getFullYear(),
          month: parseInt(this.month) || new Date().getMonth() + 1,
          trend_type: trendType
        };

        console.log('请求健康焦点参数:', params);

        const res = await post('/api/v1/health-focus', params);

        console.log('健康焦点接口返回:', res);

        if (res) {
          this.healthFocusData = res;
          console.log('设置健康焦点数据:', this.healthFocusData);

          // 数据更新后重新渲染图表
          this.$nextTick(() => {
            if (this.healthFocusData) {
              this.initFocusChart();
            }
          });
        }
      } catch (error) {
        console.error('获取健康焦点数据失败:', error);
      } finally {
        this.checkAllLoaded();
      }
    },
    
    async fetchMonitoringStats() {
      try {
        const params = {
          user_id: parseInt(this.userId) || 1,
          year: parseInt(this.year) || new Date().getFullYear(),
          month: parseInt(this.month) || new Date().getMonth() + 1
        };
        
        console.log('请求监测统计参数:', params);
        
        const res = await post('/api/v1/monthly-monitoring-stats', params);
        
        console.log('监测统计接口返回:', res);
        
        if (res) {
          this.monitoringStats = res;
          console.log('设置监测统计数据:', this.monitoringStats);
        }
      } catch (error) {
        console.error('获取监测统计数据失败:', error);
      } finally {
        this.checkAllLoaded();
      }
    },
    
    async fetchTrendData() {
      try {
        const params = {
          user_id: parseInt(this.userId) || 1,
          year: parseInt(this.year) || new Date().getFullYear(),
          month: parseInt(this.month) || new Date().getMonth() + 1
        };
        
        console.log('请求参数:', params);
        
        const res = await post('/api/v1/monthly-health-trend', params);
        
        console.log('接口返回:', res);
        
        if (res) {
          this.trendData = res;
          console.log('设置数据:', this.trendData);
        }
      } catch (error) {
        console.error('获取月度趋势数据失败:', error);
      } finally {
        this.checkAllLoaded();
      }
    },
    
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return (date.getMonth() + 1) + '/' + date.getDate();
    },
    
    formatScore(score) {
      return score >= 0 ? '+' + score.toFixed(2) : score.toFixed(2);
    },
    
    formatMonthDay(dateStr) {
      const date = new Date(dateStr);
      if (this.$i18n && this.$i18n.locale === 'en-US') {
        return new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric'
        }).format(date);
      }
      return (date.getMonth() + 1) + '月' + date.getDate() + '日';
    },
    
    processAIText(text, highlights) {
      let result = text;
      const colors = ['#FF5C4D', '#003CBF'];
      let colorIndex = 0;
      
      Object.keys(highlights).sort().forEach(key => {
        const highlight = highlights[key];
        const color = colors[colorIndex % colors.length];
        result = result.replace(
          '{$' + key + '$}',
          '<span style="color:' + color + ';">' + highlight.text + '</span>'
        );
        colorIndex++;
      });
      return result;
    },
    
    initTrendChart() {
      console.log('initTrendChart 被调用, trendData:', this.trendData);
      
      if (!this.trendData) {
        console.log('trendData 为空，退出');
        return;
      }
      
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }
      
      this.chartInstance = this.$echarts.init(this.$refs.trendChart);
      
      const { smoothed_data, statistics } = this.trendData;
      
      // 使用smoothed_data的数据
      const allDates = smoothed_data.map(item => this.formatDate(item.date));
      const originalScores = smoothed_data.map(item => item.original_score);
      const smoothedScores = smoothed_data.map(item => item.smoothed_score);
      
      // 找出最高点和最低点的索引
      const maxIndex = smoothed_data.findIndex(item => item.date === statistics.max_point_date);
      const minIndex = smoothed_data.findIndex(item => item.date === statistics.min_point_date);
      
      const option = {
        grid: {
          left: '12%',
          right: '5%',
          top: '10%',
          bottom: '15%',
          containLabel: false
        },
        xAxis: {
          type: 'category',
          data: allDates,
          boundaryGap: true,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA'
            }
          },
          axisTick: {
            show: true,
            alignWithLabel: true,
            lineStyle: {
              color: '#EAEAEA'
            }
          },
          axisLabel: {
            color: '#666666'
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA',
              type: 'dashed'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA'
            }
          },
          axisTick: {
            show: true,
            lineStyle: {
              color: '#EAEAEA'
            }
          },
          axisLabel: {
            color: '#666666'
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            data: originalScores,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: 'rgba(153, 153, 153, 0.26)',
              type: 'dashed',
              width: 2
            },
            showSymbol: false
          },
          {
            data: smoothedScores,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: '#003CBF',
              width: 2
            },
            showSymbol: false
          },
          {
            name: this.$t('monthlyReport.messages.lowestValue'),
            type: 'scatter',
            data: minIndex >= 0 ? [[minIndex, smoothedScores[minIndex]]] : [],
            itemStyle: {
              color: '#FF5C4D'
            },
            symbolSize: 10,
            z: 10
          },
          {
            name: this.$t('monthlyReport.messages.highestValue'),
            type: 'scatter',
            data: maxIndex >= 0 ? [[maxIndex, smoothedScores[maxIndex]]] : [],
            itemStyle: {
              color: '#003CBF'
            },
            symbolSize: 10,
            z: 10
          }
        ]
      };
      
      this.chartInstance.setOption(option);
      
      // 自适应
      window.addEventListener('resize', () => {
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      });
    },
    
    initFocusChart() {
      if (this.focusChartInstance) {
        this.focusChartInstance.dispose();
      }
      
      this.focusChartInstance = this.$echarts.init(this.$refs.focusChart);
      
      if (!this.healthFocusData) {
        return;
      }
      
      const { total_score_line, constitution_line, nutrition_line } = this.healthFocusData;
      
      // 处理总分数据，筛选与constitution_line和nutrition_line日期匹配的数据
      const targetDates = constitution_line.map(item => item.date);
      const filteredTotalScore = total_score_line.filter(item => targetDates.includes(item.date));
      
      const dates = filteredTotalScore.map(item => this.formatDate(item.date));
      const data1 = nutrition_line.map(item => item.score);
      const data2 = constitution_line.map(item => item.score);
      const data3 = filteredTotalScore.map(item => item.total_score);

      // 根据activeTab设置颜色
      const colors = this.activeTab === 'decline'
        ? { line1: '#FF5C4D', line2: '#FFCFCA' }  // 下滑期：红色系
        : { line1: '#003CBF', line2: '#5087FF' };  // 回升期：蓝色系

      const option = {
        grid: {
          left: '12%',
          right: '5%',
          top: '10%',
          bottom: '15%',
          containLabel: false
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666666'
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA',
              type: 'dashed'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666666'
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#EAEAEA',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            data: data1,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: colors.line1,
              width: 3
            },
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: colors.line1
            }
          },
          {
            data: data2,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: colors.line2,
              width: 3
            },
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: colors.line2
            }
          },
          {
            data: data3,
            type: 'line',
            smooth: true,
            lineStyle: {
              color: '#E0E0E0',
              width: 3
            },
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#E0E0E0'
            }
          }
        ]
      };
      
      this.focusChartInstance.setOption(option);
      
      // 自适应
      window.addEventListener('resize', () => {
        if (this.focusChartInstance) {
          this.focusChartInstance.resize();
        }
      });
    },
    hideAIChat() {
      const aiIcon = document.querySelector('.ai-icon');
      if (aiIcon && aiIcon.parentElement) {
        aiIcon.parentElement.style.display = 'none';
      }
    },
    showAIChat() {
      const aiIcon = document.querySelector('.ai-icon');
      if (aiIcon && aiIcon.parentElement) {
        aiIcon.parentElement.style.display = '';
      }
    }
  }
};
</script>

<style scoped>
.monthly-report {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 4vw 3vw;
  box-sizing: border-box;
  max-width: 750px;
  margin: 0 auto;
}

.module {
  margin-bottom: 8vw;
  width: 100%;
  box-sizing: border-box;
}

.module-title {
  font-size: 4.8vw;
  color: #1A1A1A;
  margin-bottom: 2vw;
  font-weight: 600;
  padding: 0 3vw;
}

.module-subtitle {
  font-size: 3.2vw;
  color: #666666;
  margin-bottom: 2vw;
  padding: 0 3vw;
  line-height: 1.6;
}

.module-content {
  background-color: #FFFFFF;
  border-radius: 4vw;
  padding: 4vw;
  box-sizing: border-box;
  width: 100%;
}

.focus-module-content {
  border-radius: 0 0 4vw 4vw;
}

.health-focus-top {
  background-color: #FFF5F4;
  border-radius: 3vw;
  padding: 4vw;
  box-sizing: border-box;
  margin-bottom: 4vw;
  display: flex;
  gap: 2vw;
  transition: background-color 0.3s ease;
}

.health-focus-top.decline-style {
  background-color: #FFF5F4;
}

.health-focus-top.rise-style {
  background-color: #F0F6FF;
}

.insight-label {
  color: #FF5C4D;
  font-size: 3.7vw;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.3s ease;
}

.decline-style .insight-label {
  color: #FF5C4D;
}

.rise-style .insight-label {
  color: #003CBF;
}

.insight-content {
  color: #666666;
  font-size: 3.7vw;
  line-height: 1.8;
  flex: 1;
}

.focus-chart-container {
  width: 90%;
  height: 50vw;
  min-height: 200px;
  margin: 0 auto;
}

.module-content p {
  margin: 0;
  color: #333;
  line-height: 1.6;
  font-size: 3.7vw;
}

.chart-container {
  width: 100%;
  height: 60vw;
  min-height: 250px;
}

.stats-row {
  display: flex;
  justify-content: space-around;
  margin-top: 4vw;
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-label {
  font-size: 3.2vw;
  color: #999999;
  margin-bottom: 1.5vw;
}

.stat-value {
  font-size: 4.5vw;
  font-weight: 600;
}

.stat-high {
  color: #003CBF;
}

.stat-low {
  color: #FF5C4D;
}

.stat-avg {
  color: #666666;
}

.stat-note {
  font-size: 2.8vw;
  color: #999999;
  margin-top: 1vw;
}

.ai-interpretation {
  margin-top: 6vw;
  padding: 0 2vw;
}

.ai-title {
  font-size: 4vw;
  color: #1A1A1A;
  font-weight: normal;
  margin-bottom: 2vw;
}

.ai-text {
  font-size: 3.5vw;
  color: #666666;
  line-height: 1.8;
  margin: 0;
}

.divider {
  margin-top: 6vw;
  margin-bottom: 8vw;
  border-top: 1px dashed #CECECE;
}

.additional-content {
  padding: 0;
  margin-bottom: 4vw;
  margin-top: 4vw;
}

.monitoring-stats {
  display: flex;
  align-items: flex-start;
}

.stats-left {
  flex: 1;
  max-width: 50%;
}

.stats-title {
  font-size: 4vw;
  color: #1A1A1A;
  font-weight: 600;
  margin-bottom: 2vw;
}

.month-highlight {
  color: #003CBF;
}

.stats-numbers {
  display: flex;
  align-items: baseline;
  margin-bottom: 1.5vw;
  margin-top: 8vw;
}

.monitoring-count {
  font-size: 5.5vw;
  color: #003CBF;
  font-weight: 600;
}

.divider-slash {
  font-size: 3.8vw;
  color: #999999;
  margin: 0 1vw;
}

.total-days {
  font-size: 3.8vw;
  color: #999999;
}

.days-unit {
  font-size: 2.8vw;
  color: #999999;
}

.stats-desc {
  font-size: 2.8vw;
  color: #999999;
  margin-bottom: 2vw;
}

.status-message {
  font-size: 2.8vw;
  color: #999999;
  line-height: 1.6;
  margin-top: 5vw;
  white-space: pre-line;
  word-break: break-all;
  max-width: 10em;
}

.status-message::first-line {
  color: #003CBF;
  font-size: 3.2vw;
}

.stats-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.date-range {
  font-size: 3.7vw;
  color: #999999;
  margin-bottom: 2vw;
  align-self: flex-end;
}

.trajectory-text {
  font-size: 3.7vw;
  color: #999999;
  margin-top: 5vw;
  margin-bottom: 2vw;
}

.trajectory-dots {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  column-gap: 1.2vw;
  row-gap: 3vw;
  justify-items: start;
}

.dot {
  width: 3.6vw;
  height: 3.6vw;
  border-radius: 50%;
  border: 1px solid #D9D9D9;
  background-color: transparent;
}

.dot.monitored {
  background-color: #DDEAFF;
  border: 1px solid transparent;
}

.chart-legend {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8vw;
  margin-top: 4vw;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 1.5vw;
}



.legend-icon {
  width: 5vw;
  height: 5vw;
  object-fit: contain;
}

.legend-text {
  font-size: 3.2vw;
  color: #666666;
}

.focus-summary {
  margin-top: 4vw;
  margin-bottom: 4vw;
  font-size: 3.5vw;
  color: #666666;
  line-height: 1.8;
}

.focus-switch-bar {
  display: flex;
  margin-bottom: 0;
  width: 100%;
  align-items: flex-end;
}

.switch-item {
  flex: 1;
  height: 16vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 4vw;
  color: #FFFFFF;
  font-weight: 500;
  gap: 0.5vw;
  cursor: pointer;
  transition: all 0.3s ease;
}

.switch-item.inactive {
  height: 13vw;
  background-color: #E7EBEF !important;
  color: #999999;
}

.switch-title {
  font-size: 3.5vw;
  font-weight: 400;
}

.switch-subtitle {
  font-size: 3.5vw;
  font-weight: 600;
  opacity: 1;
}

.switch-left {
  background-color: #FF5C4D;
  border-radius: 2vw 2vw 0 0;
}

.switch-right {
  background-color: #003CBF;
  border-radius: 2vw 2vw 0 0;
}

.improvement-item {
  margin-bottom: 4vw;
  padding: 0 2vw;
}

.improvement-item:last-child {
  margin-bottom: 0;
}

.improvement-text {
  font-size: 3.5vw;
  color: #666666;
  line-height: 1.8;
  margin: 0;
  white-space: pre-line;
}

.lifestyle-suggestions {
  margin-top: 6vw;
}

.lifestyle-title {
  font-size: 4vw;
  color: #2C324E;
  font-weight: 600;
  margin-bottom: 4vw;
}

.suggestion-item {
  display: flex;
  margin-bottom: 4vw;
  padding: 0;
}

.suggestion-icon {
  width: 9vw;
  height: 9vw;
  margin-right: 3vw;
  flex-shrink: 0;
}

.suggestion-content {
  flex: 1;
  padding-right: 2vw;
}

.suggestion-topic {
  font-size: 3.7vw;
  color: #2C324E;
  font-weight: 600;
  margin-bottom: 1.5vw;
}

.suggestion-text {
  font-size: 3.5vw;
  color: #495880;
  line-height: 1.8;
}

.custom-solution-button-container {
  padding: 5vw 3vw 8vw 3vw;
  margin: -2vw 0 0 0;
}

.custom-solution-button {
  width: 100%;
  height: 12vw;
  background-color: #003CBF;
  color: #FFFFFF;
  border: none;
  border-radius: 6vw;
  font-size: 3.7vw;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-solution-button:active {
  opacity: 0.8;
}

.disclaimer {
  text-align: center;
  color: #999999;
  font-size: 2.6vw;
  line-height: 1.5;
  padding: 0 3vw 4vw;
  margin-top: -4vw;
}

.disclaimer p {
  margin: 0;
  color: #999999;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}

.spinner {
  width: 12vw;
  height: 12vw;
  border: 1vw solid #E0E0E0;
  border-top-color: #003CBF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 4vw;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-title {
  font-size: 4.5vw;
  color: #1A1A1A;
  margin: 0 0 3vw 0;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.loading-subtitle {
  font-size: 3.5vw;
  color: #666666;
  margin: 0;
  min-height: 5vw;
  opacity: 0.8;
}

.loading-overlay.fade-out {
  animation: fadeOut 0.5s ease-out forwards;
}

@keyframes fadeOut {
  to {
    opacity: 0;
  }
}

@media (min-width: 750px) {
  .monthly-report {
    padding: 30px 22.5px;
  }
  
  .module {
    margin-bottom: 60px;
  }
  
  .module-title {
    font-size: 36px;
    margin-bottom: 15px;
    padding: 0 22.5px;
  }
  
  .module-subtitle {
    font-size: 28px;
    margin-bottom: 15px;
    padding: 0 22.5px;
  }
  
  .module-content {
    border-radius: 30px;
    padding: 30px;
  }

  .focus-module-content {
    border-radius: 0 0 30px 30px;
  }

  .health-focus-top {
    border-radius: 22px;
    padding: 30px;
    margin-bottom: 30px;
    gap: 15px;
  }
  
  .insight-label {
    font-size: 28px;
  }
  
  .insight-content {
    font-size: 28px;
  }
  
  .focus-chart-container {
    width: 90%;
    height: 350px;
  }
  
  .module-content p {
    font-size: 28px;
  }
  
  .chart-container {
    height: 450px;
  }
  
  .stats-row {
    margin-top: 30px;
  }
  
  .stat-label {
    font-size: 24px;
    margin-bottom: 12px;
  }
  
  .stat-value {
    font-size: 34px;
  }
  
  .stat-note {
    font-size: 21px;
    margin-top: 8px;
  }
  
  .ai-interpretation {
    margin-top: 45px;
    padding: 0 15px;
  }
  
  .ai-title {
    font-size: 30px;
    margin-bottom: 15px;
  }
  
  .ai-text {
    font-size: 26px;
  }
  
  .divider {
    margin-top: 45px;
    margin-bottom: 60px;
  }
  
  .additional-content {
    padding: 0;
    margin-bottom: 30px;
    margin-top: 30px;
  }
  
  .stats-left {
    max-width: 50%;
  }
  
  .stats-title {
    font-size: 30px;
    margin-bottom: 15px;
  }
  
  .stats-numbers {
    margin-bottom: 12px;
    margin-top: 60px;
  }
  
  .monitoring-count {
    font-size: 41px;
  }
  
  .divider-slash {
    font-size: 28px;
    margin: 0 8px;
  }
  
  .total-days {
    font-size: 28px;
  }
  
  .days-unit {
    font-size: 21px;
  }
  
  .stats-desc {
    font-size: 21px;
    margin-bottom: 15px;
  }
  
  .status-message {
    font-size: 21px;
    margin-top: 38px;
    max-width: 10em;
  }
  
  .status-message::first-line {
    font-size: 24px;
  }
  

  
  .date-range {
    font-size: 28px;
    margin-bottom: 12px;
  }
  
  .trajectory-text {
    font-size: 28px;
    margin-top: 38px;
    margin-bottom: 15px;
  }
  
  .trajectory-dots {
    column-gap: 9px;
    row-gap: 22px;
  }
  
  .dot {
    width: 27px;
    height: 27px;
  }
  
  .chart-legend {
    margin-top: 30px;
    gap: 60px;
  }
  
  .legend-item {
    gap: 11px;
  }
  
  .legend-icon {
    width: 19px;
    height: 19px;
  }
  
  .legend-text {
    font-size: 24px;
  }
  
  .focus-summary {
    margin-top: 30px;
    margin-bottom: 30px;
    font-size: 26px;
  }

  .focus-switch-bar {
    margin-bottom: 0;
  }

  .switch-item {
    height: 120px;
    font-size: 30px;
    gap: 4px;
  }

  .switch-item.inactive {
    height: 98px;
  }

  .switch-title {
    font-size: 26px;
  }

  .switch-subtitle {
    font-size: 26px;
  }

  .switch-left {
    border-radius: 15px 15px 0 0;
  }

  .switch-right {
    border-radius: 15px 15px 0 0;
  }

  .improvement-item {
    margin-bottom: 30px;
    padding: 0 15px;
  }
  
  .improvement-text {
    font-size: 26px;
  }
  
  .lifestyle-suggestions {
    margin-top: 45px;
  }
  
  .lifestyle-title {
    font-size: 30px;
    margin-bottom: 30px;
  }
  
  .suggestion-item {
    margin-bottom: 30px;
  }
  
  .suggestion-icon {
    width: 70px;
    height: 70px;
    margin-right: 22px;
  }
  
  .suggestion-content {
    padding-right: 15px;
  }
  
  .suggestion-topic {
    font-size: 28px;
    margin-bottom: 12px;
  }
  
  .suggestion-text {
    font-size: 26px;
  }

  .custom-solution-button-container {
    padding: 38px 22.5px 60px 22.5px;
    margin: -15px 0 0 0;
  }

  .custom-solution-button {
    height: 90px;
    border-radius: 45px;
    font-size: 28px;
  }

  .disclaimer {
    font-size: 20px;
    padding: 0 22.5px 30px;
    margin-top: -30px;
  }
  
  .spinner {
    width: 90px;
    height: 90px;
    border-width: 8px;
    margin-bottom: 30px;
  }
  
  .loading-title {
    font-size: 34px;
    margin-bottom: 22px;
  }
  
  .loading-subtitle {
    font-size: 26px;
    min-height: 38px;
  }
}
</style>

<style>
/* 隐藏AI小助手 */
.monthly-report ~ .ai-chat-container {
  display: none !important;
}
</style>
